import type {ConditionalAmountGroupNode} from "#pricing/nodes/conditional-amount-group-node"
import type {ConfigurationGroupNode} from "#pricing/nodes/configuration-group-node"
import type {ConfigVariantRule} from "#pricing/nodes/config-variant-condition"
import {ConfigVariantCondition} from "#pricing/nodes/config-variant-condition"
import {ConfigurationList} from "#pricing/nodes/configuration-list"
import type {PricingNodegraph} from "#pricing/types/pricing-types"
import {PriceContainer} from "#pricing/nodes/price-container"
import {PriceList} from "#pricing/nodes/price-list"
import type {PricedItemNode} from "#pricing/nodes/priced-item-node"
import {VariantConditionNode} from "#pricing/nodes/variant-condition-node"
import {VariantSelectedCondition} from "#pricing/nodes/variant-selected-condition"
import {BooleanOperation} from "#pricing/nodes/boolean-operation"
import type {Catalog, CatalogItem} from "#pricing/catalogs/catalog-interface"
import type {PricingContext} from "#pricing/pricing-context"
import type {ParameterValue} from "@cm/graph-v3"

export interface PriceGraphData {
    amountGroups: ConditionalAmountGroupNode[]
    pricedItemNodes: PricedItemNode[]
    priceContainers: PriceContainer[]
}

/**
 * Searches the catalog for items matching all `terms` (AND semantics), drops items whose description contains any of
 * `termExclude` and asserts that exactly `expectedAmount` items remain.
 */
export function searchCatalogItems(catalog: Catalog, terms: string | string[], expectedAmount: number, termExclude?: string[]): CatalogItem[] {
    const termList = Array.isArray(terms) ? terms : [terms]
    if (termList.length === 0) throw new Error("searchCatalogItems requires at least one search term")

    let searchResults = catalog.search(termList[0])
    for (const term of termList.slice(1)) {
        const matches = new Set(catalog.search(term))
        searchResults = searchResults.filter((item) => matches.has(item))
    }

    if (termExclude) searchResults = searchResults.filter((item) => !termExclude.some((excludeTerm) => item.description.includes(excludeTerm)))

    if (searchResults.length !== expectedAmount) {
        throw new Error(`Expected ${expectedAmount} search results for terms ${termList.join(", ")}, got ${searchResults.length}`)
    }

    return searchResults
}

/**
 * Creates a priced item node without a variant condition for the single catalog item matching `terms`. The node is
 * priced solely by `amount` (e.g. a MeshAmountNode counting placed meshes).
 */
export function createAmountTriggeredPriceNode(catalog: Catalog, terms: string | string[], amount: ParameterValue<number, PricingContext>): PricedItemNode {
    const [item] = searchCatalogItems(catalog, terms, 1)
    const pricedItemNode = item.createPriceGraph((uniqueId: string) => undefined, false)
    pricedItemNode.updateParameters({amount})
    return pricedItemNode
}

/** @deprecated Builds VariantConditionNodes. Use createKeyedVariantPriceNodes or createConditionedPriceNodes instead. */
export function createVariantConditionedPriceNodes(
    catalog: Catalog,
    terms: string | string[],
    expectedAmount: number,
    configMap?: Map<string, [ConfigurationGroupNode, string]>,
    amount?: ParameterValue<number, PricingContext>,
    termExclude?: string[],
): PricedItemNode[] {
    return searchCatalogItems(catalog, terms, expectedAmount, termExclude).map((item) => {
        const pricedItemNode = item.createPriceGraph((uniqueId: string) => undefined)

        if (amount) pricedItemNode.updateParameters({amount})

        if (configMap) {
            let found = false
            configMap.forEach((value, key) => {
                if (pricedItemNode.parameters.description.includes(key)) {
                    if (found) throw new Error(`Key ${key} already found`)
                    found = true
                    pricedItemNode.addDependency(value[0], value[1], "")
                }
            })

            if (!found) {
                throw new Error(`No config variant found for priced item node with description: ${pricedItemNode.parameters.description}`)
            }
        }

        return pricedItemNode
    })
}

export type Condition = ParameterValue<boolean, PricingContext>

/**
 * True if the current variant of the active group is one of `variantIds`. Usually one group; several only for mutually exclusive groups
 * that share their variant ids (funnel, see Conditions.md, case 2).
 */
export function variantSelected(groups: ConfigurationGroupNode | ConfigurationGroupNode[], variantIds: string | string[]): VariantSelectedCondition {
    return new VariantSelectedCondition({
        parameters: {groups: Array.isArray(groups) ? groups : [groups], variantIds: Array.isArray(variantIds) ? variantIds : [variantIds]},
    })
}

/** Note: `and` of two conditions on the same group is always false; use one variantSelected with several ids instead. */
export function and(...inputs: Condition[]): BooleanOperation {
    return new BooleanOperation({parameters: {operator: "and", inputs}})
}

export function or(...inputs: Condition[]): BooleanOperation {
    return new BooleanOperation({parameters: {operator: "or", inputs}})
}

/** A variantSelected whose groups are not part of the configuration is false, so its negation is true. */
export function not(input: Condition): BooleanOperation {
    return new BooleanOperation({parameters: {operator: "not", inputs: [input]}})
}

/**
 * Creates priced item nodes for the items matching `terms` (see searchCatalogItems), all with the same `condition` or, if a function is
 * given, with one condition per item (e.g. `(item) => variantSelected(BasinFinish, item.uniqueId)` if the variant ids equal the SKUs).
 */
export function createConditionedPriceNodes(
    catalog: Catalog,
    terms: string | string[],
    expectedAmount: number,
    condition: Condition | ((item: CatalogItem) => Condition),
    amount?: ParameterValue<number, PricingContext>,
    termExclude?: string[],
): PricedItemNode[] {
    return searchCatalogItems(catalog, terms, expectedAmount, termExclude).map((item) => {
        const pricedItemNode = item.createPriceGraph((uniqueId: string) => undefined, false)
        pricedItemNode.updateParameters({condition: typeof condition === "function" ? condition(item) : condition})
        if (amount) pricedItemNode.updateParameters({amount})
        return pricedItemNode
    })
}

/**
 * Links catalog entries to config variants: a catalog entry containing the key "Pearly White" is priced if the config variant
 * "pearly-white" is selected.
 */
export type KeyedVariants = {
    //the config group(s) the variant is read from; usually one. Several only for mutually exclusive groups that share their variant ids
    //(e.g. one accessory finish group per handle design, funnel, see Conditions.md, case 2)
    groups: ConfigurationGroupNode[]
    //key in the catalog entry → config variant id, e.g. "Pearly White" → "pearly-white"
    variantIdByKey: Map<string, string>
    //where the key is searched: in the description (default), or at the end of the SKU for descriptions that contain each other
    //(e.g. "Brushed Brass" ⊂ "Natural Brushed Brass": use the SKU suffixes "BBR" / "NBB" as keys)
    keyMatches?: "description" | "skuSuffix"
}

/**
 * Creates one priced item node per catalog entry matching `terms`, each priced only while the config variant linked to it by `variants`
 * is selected. Every entry must match exactly one key. An additional `condition` is combined with `and` (e.g. the vanity size of a
 * finish-dependent unit).
 */
export function createKeyedVariantPriceNodes(
    catalog: Catalog,
    terms: string | string[],
    expectedAmount: number,
    variants: KeyedVariants,
    options: {amount?: ParameterValue<number, PricingContext>; termExclude?: string[]; condition?: Condition} = {},
): PricedItemNode[] {
    const {amount, termExclude, condition} = options
    return createConditionedPriceNodes(
        catalog,
        terms,
        expectedAmount,
        (item) => {
            const matches = [...variants.variantIdByKey].filter(([key]) =>
                variants.keyMatches === "skuSuffix" ? item.uniqueId.endsWith(key) : item.description.includes(key),
            )
            if (matches.length !== 1) throw new Error(`Expected one variant key for ${item.uniqueId} (${item.description}), got ${matches.length}`)

            const selected = variantSelected(variants.groups, matches[0][1])
            return condition !== undefined ? and(selected, condition) : selected
        },
        amount,
        termExclude,
    )
}

function evaluateRuleAgainstString(rule: ConfigVariantRule, text: string): boolean {
    let actual = text
    let expected = rule.value
    if (rule.caseInsensitive) {
        actual = actual.toLowerCase()
        expected = expected.toLowerCase()
    }
    switch (rule.operator) {
        case "equals":
            return actual === expected
        case "contains":
            return actual.includes(expected)
        case "notContains":
            return !actual.includes(expected)
    }
}

/**
 * Creates priced item nodes whose condition is a ConfigVariantCondition: each item gets the single rule group (from
 * `configVariantRules.rules`) that matches its description; zero or multiple matching groups are an error.
 */
export function createRuleConditionedPriceNodes(
    catalog: Catalog,
    term: string,
    expectedAmount: number,
    configVariantRules: {configGroup: ConfigurationGroupNode; rules: ConfigVariantRule[][]},
    termExclude?: string[],
): PricedItemNode[] {
    return searchCatalogItems(catalog, term, expectedAmount, termExclude).map((item) => {
        const pricedItemNode = item.createPriceGraph((uniqueId: string) => undefined)

        let found = false
        configVariantRules.rules.forEach((ruleGroup) => {
            const allMatch = ruleGroup.every((rule) => evaluateRuleAgainstString(rule, pricedItemNode.parameters.description))
            if (allMatch) {
                if (found) throw new Error(`Multiple rule groups matched description: ${pricedItemNode.parameters.description}`)
                found = true
                const node = new ConfigVariantCondition({parameters: {variant: configVariantRules.configGroup, rules: ruleGroup}})
                pricedItemNode.updateParameters({condition: node})
            }
        })

        if (!found) {
            throw new Error(`No rule found to match: ${pricedItemNode.parameters.description}`)
        }

        return pricedItemNode
    })
}

/**
 * New mappings always pass `condition` (`true` for mesh-triggered containers, or e.g. a variantSelected). Without it, the container gets an
 * empty (deprecated) VariantConditionNode, which is false until `addDependency` is called; kept for the existing mappings.
 */
export function createPriceContainer(
    description: string,
    uniqueId: string,
    subPrices: PricingNodegraph[],
    condition?: ParameterValue<boolean, PricingContext>,
): PriceContainer {
    return new PriceContainer({
        parameters: {
            description: description,
            uniqueId: uniqueId,
            subPrices: new PriceList({parameters: {list: [...subPrices]}}),
            condition:
                condition !== undefined
                    ? condition
                    : new VariantConditionNode({
                          parameters: {
                              condition: {variantIds: [], variantOperator: "AND", negated: false},
                              currentConfigurations: new ConfigurationList({parameters: {list: []}}),
                          },
                      }),
        },
    })
}
